import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * Component for text item controls with enhanced styling options
 */
const TextItemEditor = ({ item, index, updateItem, deleteItem, setActiveItem, isActive }) => {
    // Load Google Fonts when component mounts
    useEffect(() => {
        // Check if Google Fonts are already loaded
        if (!document.querySelector('#google-fonts-link')) {
            const link = document.createElement('link');
            link.id = 'google-fonts-link';
            link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap';
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
    }, []);

    // Position presets for quick selection
    const positionPresets = [
        { name: 'Center', x: 50, y: 50 },
        { name: 'Top', x: 50, y: 20 },
        { name: 'Bottom', x: 50, y: 80 },
        { name: 'Left', x: 20, y: 50 },
        { name: 'Right', x: 80, y: 50 }
    ];
    
    // Predefined font families with display names - Updated with working cursive fonts
    const fontFamilies = [
        { value: "Arial, sans-serif", label: "Sans-serif" },
        { value: "'Times New Roman', serif", label: "Serif" },
        { value: "'Courier New', monospace", label: "Monospace" },
        { value: "Georgia, serif", label: "Georgia" },
        { value: "'Playfair Display', serif", label: "Display" },
        { value: "'Dancing Script', cursive", label: "Cursive" },
        { value: "cursive", label: "System Cursive" }
    ];
    
    // Expanded predefined color options - same as Step6.js
    const colorOptions = [
        // Row 1
        { name: 'White', value: 'white' },
        { name: 'Black', value: 'black' },
        { name: 'Gray', value: 'gray' },
        { name: 'Red', value: '#c12f2d' },
        { name: 'Maroon', value: '#a52c48' },
        { name: 'Wine', value: '#872434' },
        { name: 'Dark Pink', value: '#e8396e' },
        { name: 'Magenta', value: '#dc3979' },
        // Row 2
        { name: 'Pink', value: 'pink' },
        { name: 'Baby Pink', value: '#fbb7d2' },
        { name: 'Peach', value: '#fa9fa3' },
        { name: 'Yellow', value: '#f3c400' },
        { name: 'Mint Green', value: '#d0f3c0' },
        { name: 'Green', value: '#1d5d37' },
        { name: 'Dark Green', value: '#1b3c37' },
        { name: 'Teal Green', value: '#30957b' },
        // Row 3
        { name: 'Cyan', value: '#147e99' },
        { name: 'Sky Blue', value: '#dbf3f9' },
        { name: 'Light Blue', value: '#eaf6f9' },
        { name: 'Blue', value: 'blue' },
        { name: 'Royal Blue', value: '#1a1fc1' },
        { name: 'Navy Blue', value: '#1e1a4d' },
        { name: 'Indigo', value: '#1f2b5e' },
        { name: 'Violet', value: '#902586' }
    ];
    
    // Helper function to get display color value
    const getDisplayColor = (colorName, customColor) => {
        if (colorName === 'custom') {
            return customColor || 'transparent';
        }
        const colorOption = colorOptions.find(c => c.value === colorName);
        return colorOption ? colorOption.value : colorName;
    };
    
    // Apply a preset position
    const applyPositionPreset = (preset) => {
        updateItem(index, { x: preset.x, y: preset.y });
    };
    
    // Handle custom color change
    const handleCustomColorChange = (type, value) => {
        const updates = {};
        
        switch(type) {
            case 'text':
                updates.color = 'custom';
                updates.customColor = value;
                break;
            case 'background':
                updates.backgroundColor = 'custom';
                updates.customBackgroundColor = value;
                break;
            case 'border':
                updates.borderColor = 'custom';
                updates.customBorderColor = value;
                break;
        }
        
        updateItem(index, updates);
    };
    
    return (
        <div className={`p-3 border mb-2 rounded ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
            <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Text {index + 1}</h4>
                <button 
                    onClick={() => deleteItem(index)}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Delete text"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            
            <div className="mb-3">
                {/* Textarea for multi-line text input */}
                <textarea
                    value={item.text}
                    onChange={(e) => updateItem(index, { text: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded resize-y"
                    placeholder="Enter text (supports multiple lines)"
                    rows={3}
                    style={{ minHeight: "80px" }}
                />
                <div className="text-xs text-gray-500 mt-1">
                    <span>Tip: Press Enter for line breaks</span>
                </div>
            </div>

            
            
            {/* Tabs for different styling options */}
            <div className="border-b border-gray-200 mb-3">
                <div className="flex -mb-px">
                    <button 
                        onClick={() => updateItem(index, { activeTab: 'text' })} 
                        className={`py-2 px-4 text-xs font-medium ${item.activeTab === 'text' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    >
                        Text Style
                    </button>
                    <button 
                        onClick={() => updateItem(index, { activeTab: 'background' })} 
                        className={`py-2 px-4 text-xs font-medium ${item.activeTab === 'background' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    >
                        Background
                    </button>
                    <button 
                        onClick={() => updateItem(index, { activeTab: 'position' })} 
                        className={`py-2 px-4 text-xs font-medium ${item.activeTab === 'position' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    >
                        Position
                    </button>
                </div>
            </div>
            
            {/* Content based on active tab */}
            {(item.activeTab === 'text' || !item.activeTab) && (
                <>
                    {/* Font Size - Now full width in its own row */}
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Size</label>
                        <input
                            type="range"
                            min="12"
                            max="72"
                            value={item.fontSize || 24}
                            onChange={(e) => updateItem(index, { fontSize: parseInt(e.target.value) })}
                            className="w-full"
                        />
                        <div className="text-xs text-center mt-1">{item.fontSize || 24}px</div>
                    </div>
                    
                    {/* Text Color - Now full width in its own row */}
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Color</label>
                        {/* Row 1 */}
                        <div className="grid grid-cols-8 gap-1 mb-1">
                            {colorOptions.slice(0, 8).map((color) => (
                                <button
                                    key={`text-${color.name}`}
                                    onClick={() => updateItem(index, { 
                                        color: color.value,
                                        customColor: ''
                                    })}
                                    className={`h-6 rounded-md border ${
                                        item.color === color.value 
                                        ? 'ring-2 ring-blue-500' 
                                        : 'border-gray-300'
                                    }`}
                                    style={{ backgroundColor: color.value }}
                                    aria-label={`${color.name} text`}
                                    title={color.name}
                                >
                                    {color.name === 'White' ? <span className="text-xs text-gray-500">W</span> : null}
                                </button>
                            ))}
                        </div>
                        {/* Row 2 */}
                        <div className="grid grid-cols-8 gap-1 mb-1">
                            {colorOptions.slice(8, 16).map((color) => (
                                <button
                                    key={`text-${color.name}`}
                                    onClick={() => updateItem(index, { 
                                        color: color.value,
                                        customColor: ''
                                    })}
                                    className={`h-6 rounded-md border ${
                                        item.color === color.value 
                                        ? 'ring-2 ring-blue-500' 
                                        : 'border-gray-300'
                                    }`}
                                    style={{ backgroundColor: color.value }}
                                    aria-label={`${color.name} text`}
                                    title={color.name}
                                />
                            ))}
                        </div>
                        {/* Row 3 */}
                        <div className="grid grid-cols-8 gap-1 mb-1">
                            {colorOptions.slice(16, 24).map((color) => (
                                <button
                                    key={`text-${color.name}`}
                                    onClick={() => updateItem(index, { 
                                        color: color.value,
                                        customColor: ''
                                    })}
                                    className={`h-6 rounded-md border ${
                                        item.color === color.value 
                                        ? 'ring-2 ring-blue-500' 
                                        : 'border-gray-300'
                                    }`}
                                    style={{ backgroundColor: color.value }}
                                    aria-label={`${color.name} text`}
                                    title={color.name}
                                />
                            ))}
                        </div>
                        
                        {/* Custom Color Option */}
                        <button
                            onClick={() => updateItem(index, { 
                                color: 'custom',
                                customColor: item.customColor || '#ff0000'
                            })}
                            className={`h-6 w-full rounded-md border mb-2 ${
                                item.color === 'custom' 
                                ? 'ring-2 ring-blue-500' 
                                : 'border-gray-300'
                            }`}
                            style={{ background: 'linear-gradient(to right, red, yellow, green, blue, purple)' }}
                            aria-label="Custom color text"
                        >
                            <span className="text-xs text-white font-bold drop-shadow">Custom</span>
                        </button>
                        
                        {item.color === 'custom' && (
                            <input
                                type="text"
                                value={item.customColor || ''}
                                onChange={(e) => handleCustomColorChange('text', e.target.value)}
                                placeholder="Color name or hex (#ff0000)"
                                className="w-full p-1 text-sm border border-gray-300 rounded-md"
                            />
                        )}
                    </div>
                
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Font</label>
                        <select
                            value={item.fontFamily || "Arial, sans-serif"}
                            onChange={(e) => updateItem(index, { fontFamily: e.target.value })}
                            className="w-full p-1 text-sm border border-gray-300 rounded"
                        >
                            {fontFamilies.map(font => (
                                <option key={font.label} value={font.value}>{font.label}</option>
                            ))}
                        </select>
                    </div>
                
                    <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id={`bold-${index}`}
                                checked={item.bold || false}
                                onChange={(e) => updateItem(index, { bold: e.target.checked })}
                                className="mr-1"
                            />
                            <label htmlFor={`bold-${index}`} className="text-xs">Bold</label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id={`italic-${index}`}
                                checked={item.italic || false}
                                onChange={(e) => updateItem(index, { italic: e.target.checked })}
                                className="mr-1"
                            />
                            <label htmlFor={`italic-${index}`} className="text-xs">Italic</label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id={`underline-${index}`}
                                checked={item.underline || false}
                                onChange={(e) => updateItem(index, { underline: e.target.checked })}
                                className="mr-1"
                            />
                            <label htmlFor={`underline-${index}`} className="text-xs">Underline</label>
                        </div>
                    </div>
                </>
            )}
            
            {item.activeTab === 'background' && (
                <>
                    <div className="mb-3">
                        <div className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                id={`bg-toggle-${index}`}
                                checked={item.backgroundColor ? true : false}
                                onChange={(e) => updateItem(index, { 
                                    backgroundColor: e.target.checked ? 'white' : null,
                                    customBackgroundColor: ''
                                })}
                                className="mr-1"
                            />
                            <label htmlFor={`bg-toggle-${index}`} className="text-xs">Add Background</label>
                        </div>
                        
                        {item.backgroundColor && (
                            <div className="ml-5">
                                <label className="block text-xs text-gray-600 mb-1">Background Color</label>
                                {/* Row 1 */}
                                <div className="grid grid-cols-8 gap-1 mb-1">
                                    {colorOptions.slice(0, 8).map((color) => (
                                        <button
                                            key={`bg-${color.name}`}
                                            onClick={() => updateItem(index, { 
                                                backgroundColor: color.value,
                                                customBackgroundColor: ''
                                            })}
                                            className={`h-6 rounded-md border ${
                                                item.backgroundColor === color.value 
                                                ? 'ring-2 ring-blue-500' 
                                                : 'border-gray-300'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            aria-label={`${color.name} background`}
                                            title={color.name}
                                        >
                                            {color.name === 'White' ? <span className="text-xs text-gray-500">W</span> : null}
                                        </button>
                                    ))}
                                </div>
                                {/* Row 2 */}
                                <div className="grid grid-cols-8 gap-1 mb-1">
                                    {colorOptions.slice(8, 16).map((color) => (
                                        <button
                                            key={`bg-${color.name}`}
                                            onClick={() => updateItem(index, { 
                                                backgroundColor: color.value,
                                                customBackgroundColor: ''
                                            })}
                                            className={`h-6 rounded-md border ${
                                                item.backgroundColor === color.value 
                                                ? 'ring-2 ring-blue-500' 
                                                : 'border-gray-300'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            aria-label={`${color.name} background`}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                                {/* Row 3 */}
                                <div className="grid grid-cols-8 gap-1 mb-1">
                                    {colorOptions.slice(16, 24).map((color) => (
                                        <button
                                            key={`bg-${color.name}`}
                                            onClick={() => updateItem(index, { 
                                                backgroundColor: color.value,
                                                customBackgroundColor: ''
                                            })}
                                            className={`h-6 rounded-md border ${
                                                item.backgroundColor === color.value 
                                                ? 'ring-2 ring-blue-500' 
                                                : 'border-gray-300'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            aria-label={`${color.name} background`}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                                
                                {/* Custom Background Color */}
                                <button
                                    onClick={() => updateItem(index, { 
                                        backgroundColor: 'custom',
                                        customBackgroundColor: item.customBackgroundColor || '#ff0000'
                                    })}
                                    className={`h-6 w-full rounded-md border mb-2 ${
                                        item.backgroundColor === 'custom' 
                                        ? 'ring-2 ring-blue-500' 
                                        : 'border-gray-300'
                                    }`}
                                    style={{ background: 'linear-gradient(to right, red, yellow, green, blue, purple)' }}
                                    aria-label="Custom color background"
                                >
                                    <span className="text-xs text-white font-bold drop-shadow">Custom</span>
                                </button>
                                
                                {item.backgroundColor === 'custom' && (
                                    <input
                                        type="text"
                                        value={item.customBackgroundColor || ''}
                                        onChange={(e) => handleCustomColorChange('background', e.target.value)}
                                        placeholder="Color name or hex (#ff0000)"
                                        className="w-full p-1 text-sm border border-gray-300 rounded-md"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    
                    <div className="mb-3">
                        <div className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                id={`border-toggle-${index}`}
                                checked={item.showBorder || false}
                                onChange={(e) => updateItem(index, { 
                                    showBorder: e.target.checked,
                                    borderColor: e.target.checked ? (item.color || 'black') : null,
                                    customBorderColor: ''
                                })}
                                className="mr-1"
                            />
                            <label htmlFor={`border-toggle-${index}`} className="text-xs">Add Border</label>
                        </div>
                        
                        {item.showBorder && (
                            <div className="ml-5">
                                <label className="block text-xs text-gray-600 mb-1">Border Color</label>
                                {/* Row 1 */}
                                <div className="grid grid-cols-8 gap-1 mb-1">
                                    {colorOptions.slice(0, 8).map((color) => (
                                        <button
                                            key={`border-${color.name}`}
                                            onClick={() => updateItem(index, { 
                                                borderColor: color.value,
                                                customBorderColor: ''
                                            })}
                                            className={`h-6 rounded-md border ${
                                                item.borderColor === color.value 
                                                ? 'ring-2 ring-blue-500' 
                                                : 'border-gray-300'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            aria-label={`${color.name} border`}
                                            title={color.name}
                                        >
                                            {color.name === 'White' ? <span className="text-xs text-gray-500">W</span> : null}
                                        </button>
                                    ))}
                                </div>
                                {/* Row 2 */}
                                <div className="grid grid-cols-8 gap-1 mb-1">
                                    {colorOptions.slice(8, 16).map((color) => (
                                        <button
                                            key={`border-${color.name}`}
                                            onClick={() => updateItem(index, { 
                                                borderColor: color.value,
                                                customBorderColor: ''
                                            })}
                                            className={`h-6 rounded-md border ${
                                                item.borderColor === color.value 
                                                ? 'ring-2 ring-blue-500' 
                                                : 'border-gray-300'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            aria-label={`${color.name} border`}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                                {/* Row 3 */}
                                <div className="grid grid-cols-8 gap-1 mb-1">
                                    {colorOptions.slice(16, 24).map((color) => (
                                        <button
                                            key={`border-${color.name}`}
                                            onClick={() => updateItem(index, { 
                                                borderColor: color.value,
                                                customBorderColor: ''
                                            })}
                                            className={`h-6 rounded-md border ${
                                                item.borderColor === color.value 
                                                ? 'ring-2 ring-blue-500' 
                                                : 'border-gray-300'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            aria-label={`${color.name} border`}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                                
                                {/* Custom Border Color */}
                                <button
                                    onClick={() => updateItem(index, { 
                                        borderColor: 'custom',
                                        customBorderColor: item.customBorderColor || '#ff0000'
                                    })}
                                    className={`h-6 w-full rounded-md border mb-2 ${
                                        item.borderColor === 'custom' 
                                        ? 'ring-2 ring-blue-500' 
                                        : 'border-gray-300'
                                    }`}
                                    style={{ background: 'linear-gradient(to right, red, yellow, green, blue, purple)' }}
                                    aria-label="Custom color border"
                                >
                                    <span className="text-xs text-white font-bold drop-shadow">Custom</span>
                                </button>
                                
                                {item.borderColor === 'custom' && (
                                    <input
                                        type="text"
                                        value={item.customBorderColor || ''}
                                        onChange={(e) => handleCustomColorChange('border', e.target.value)}
                                        placeholder="Color name or hex (#ff0000)"
                                        className="w-full p-1 text-sm border border-gray-300 rounded-md"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
            
            {item.activeTab === 'position' && (
                <>
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-2">Quick Position</label>
                        <div className="grid grid-cols-3 gap-1">
                            {positionPresets.map(preset => (
                                <button
                                    key={preset.name}
                                    onClick={() => applyPositionPreset(preset)}
                                    className="py-1 px-2 border border-gray-300 rounded hover:bg-gray-100 text-xs"
                                >
                                    {preset.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Position</label>
                        
                        {/* Position preview - helps visualize text placement */}
                        <div className="w-full h-[60px] bg-gray-100 border border-gray-300 rounded-md mb-2 relative">
                            {/* Position indicator */}
                            <div 
                                className="absolute w-3 h-3 bg-red-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 z-10"
                                style={{ 
                                    left: `${item.x || 50}%`, 
                                    top: `${item.y || 50}%`,
                                    boxShadow: '0 0 0 2px white'
                                }}
                            ></div>
                            
                            {/* Text label */}
                            <div 
                                className="absolute transform -translate-x-1/2 -translate-y-1/2 text-[8px] bg-white px-1 border border-gray-300 rounded pointer-events-none"
                                style={{ 
                                    left: `${item.x || 50}%`, 
                                    top: `${item.y || 50}%`,
                                    marginTop: '10px'
                                }}
                            >
                                {item.text?.substring(0, 10)}
                            </div>
                            
                            {/* Guide lines */}
                            <div className="absolute inset-0 pointer-events-none">
                                <div className="absolute left-1/2 top-0 bottom-0 border-l border-dashed border-gray-400 opacity-30"></div>
                                <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-gray-400 opacity-30"></div>
                            </div>
                        </div>
                        
                        {/* X position slider */}
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs w-5">X:</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={item.x || 50}
                                onChange={(e) => updateItem(index, { x: parseInt(e.target.value) })}
                                className="flex-grow"
                            />
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={item.x || 50}
                                onChange={(e) => updateItem(index, { x: parseInt(e.target.value) })}
                                className="w-14 p-1 text-sm border border-gray-300 rounded"
                            />
                        </div>
                        
                        {/* Y position slider */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs w-5">Y:</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={item.y || 50}
                                onChange={(e) => updateItem(index, { y: parseInt(e.target.value) })}
                                className="flex-grow"
                            />
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={item.y || 50}
                                onChange={(e) => updateItem(index, { y: parseInt(e.target.value) })}
                                className="w-14 p-1 text-sm border border-gray-300 rounded"
                            />
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Rotation ({item.rotation || 0}°)</label>
                        <input
                            type="range"
                            min="0"
                            max="360"
                            value={item.rotation || 0}
                            onChange={(e) => updateItem(index, { rotation: parseInt(e.target.value) })}
                            className="w-full"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default TextItemEditor;
